import { ApiError, listArchiveEvents, type ArchiveEventItem, type EventType } from "@/api";
import { VorgangList } from "@/components/VorgangList";
import { Button } from "@/components/ui/Button";
import { EVENT_TYPES, EVENT_TYPE_LABEL } from "@/lib/labels";
import { cn } from "@/lib/cn";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

const PAGE_SIZE = 50;
const FIRST_YEAR = 2001;

const selectClass =
  "h-8 rounded-md border border-hairline bg-surface px-2 text-sm text-ink focus:outline-none focus:ring-1 focus:ring-accent";

export function ArchivePage() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? "";
  const typeParam = params.get("type");
  const type = EVENT_TYPES.find((t) => t === typeParam) as EventType | undefined;
  const yearParam = Number(params.get("year"));
  const year = Number.isInteger(yearParam) && yearParam >= FIRST_YEAR ? yearParam : undefined;
  const sourceParam = params.get("source");
  const source = sourceParam === "legacy" || sourceParam === "edicta" ? sourceParam : undefined;

  const [search, setSearch] = useState(q);
  const [items, setItems] = useState<ArchiveEventItem[] | null>(null);
  const [total, setTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestId = useRef(0);

  const setParam = useCallback(
    (key: string, value: string) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) next.set(key, value);
          else next.delete(key);
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  // Suche entprellen und in die URL schreiben.
  useEffect(() => {
    if (search === q) return;
    const timer = setTimeout(() => setParam("q", search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search, q, setParam]);

  // Bei Änderung der Filter neu laden.
  useEffect(() => {
    const id = ++requestId.current;
    setItems(null);
    setError(null);
    listArchiveEvents({ limit: PAGE_SIZE, offset: 0, q: q || undefined, type, year, source })
      .then((page) => {
        if (id !== requestId.current) return;
        setItems(page.items);
        setTotal(page.total);
      })
      .catch((err: unknown) => {
        if (id !== requestId.current) return;
        setItems([]);
        setTotal(0);
        setError(err instanceof ApiError ? err.message : "Archiv konnte nicht geladen werden.");
      });
  }, [q, type, year, source]);

  function loadMore() {
    if (!items) return;
    const id = requestId.current;
    setLoadingMore(true);
    listArchiveEvents({ limit: PAGE_SIZE, offset: items.length, q: q || undefined, type, year, source })
      .then((page) => {
        if (id !== requestId.current) return;
        setItems((prev) => [...(prev ?? []), ...page.items]);
        setTotal(page.total);
      })
      .catch((err: unknown) => {
        if (id !== requestId.current) return;
        setError(err instanceof ApiError ? err.message : "Weitere Vorgänge konnten nicht geladen werden.");
      })
      .finally(() => setLoadingMore(false));
  }

  const filtered = Boolean(q || type || year || source);
  const currentYear = new Date().getFullYear();
  const years: number[] = [];
  for (let y = currentYear; y >= FIRST_YEAR; y--) years.push(y);

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-4">
      <h1 className="mb-3 text-base font-semibold">Archiv</h1>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Suchen in Betreff und Text"
          aria-label="Suche"
          className={cn(selectClass, "min-w-0 flex-1 basis-48 placeholder:text-muted")}
        />
        <select
          aria-label="Typ"
          value={type ?? ""}
          onChange={(e) => setParam("type", e.target.value)}
          className={selectClass}
        >
          <option value="">Alle Typen</option>
          {EVENT_TYPES.map((t) => (
            <option key={t} value={t}>
              {EVENT_TYPE_LABEL[t]}
            </option>
          ))}
        </select>
        <select
          aria-label="Jahr"
          value={year ?? ""}
          onChange={(e) => setParam("year", e.target.value)}
          className={selectClass}
        >
          <option value="">Alle Jahre</option>
          {years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
        <select
          aria-label="Quelle"
          value={source ?? ""}
          onChange={(e) => setParam("source", e.target.value)}
          className={selectClass}
        >
          <option value="">Alle</option>
          <option value="edicta">edicta</option>
          <option value="legacy">Mailman-Archiv</option>
        </select>
      </div>

      {error ? <p className="mb-3 text-sm text-danger">{error}</p> : null}

      {items !== null ? (
        <p className="mb-2 text-[11px] text-muted">
          {total} {filtered ? "Treffer" : total === 1 ? "Vorgang" : "Vorgänge"}
        </p>
      ) : null}

      {items === null ? (
        <div className="space-y-2">
          <div className="h-10 animate-pulse rounded-md bg-surface-subtle" />
          <div className="h-10 animate-pulse rounded-md bg-surface-subtle" />
          <div className="h-10 animate-pulse rounded-md bg-surface-subtle" />
        </div>
      ) : null}

      {items && items.length === 0 && !error ? (
        <div className="rounded-lg border border-dashed border-hairline bg-surface px-6 py-12 text-center text-sm text-muted">
          Kein Vorgang gefunden.
        </div>
      ) : null}

      {items && items.length > 0 ? (
        <VorgangList items={items} />
      ) : null}

      {items && items.length < total ? (
        <div className="mt-3 flex justify-center">
          <Button onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? "Lädt…" : "Weitere laden"}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
